/**
 * 无限滚动加载 Composable
 * 用于文章列表的触底加载更多功能
 * 支持客户端缓存，避免路由切换时重复请求
 */

interface CachedData<T> {
  items: T[]
  total: number
  timestamp: number
}

// 默认缓存 30 分钟
const DEFAULT_CACHE_DURATION = 30 * 60 * 1000

export const useInfiniteScroll = <T>(options: {
  fetchFn: (offset: number, limit: number) => Promise<{ items: T[]; total: number }>
  cacheKey: string // 缓存的唯一标识
  limit?: number
  threshold?: number // 距离底部多少像素时触发
  cacheDuration?: number // 缓存有效期（毫秒）
}) => {
  const { fetchFn, cacheKey, limit = 20, threshold = 200, cacheDuration = DEFAULT_CACHE_DURATION } = options

  // 使用 useState 做全局缓存
  const cache = useState<CachedData<T> | null>(`infinite-scroll-${cacheKey}`, () => null)

  const items = ref<T[]>([]) as Ref<T[]>
  const total = ref(0)
  const offset = ref(0)
  const isLoading = ref(false)
  const isLoadingMore = ref(false)
  const error = ref<Error | null>(null)
  const hasMore = computed(() => items.value.length < total.value)

  // 检查缓存是否有效
  const isCacheValid = (): boolean => {
    if (!cache.value) return false
    return Date.now() - cache.value.timestamp < cacheDuration
  }

  // 更新缓存
  const updateCache = () => {
    cache.value = {
      items: items.value,
      total: total.value,
      timestamp: Date.now()
    }
  }

  // 初始加载（支持缓存）
  const loadInitial = async () => {
    // 如果缓存有效，直接使用缓存
    if (isCacheValid() && cache.value) {
      items.value = cache.value.items
      total.value = cache.value.total
      offset.value = cache.value.items.length
      return
    }

    isLoading.value = true
    error.value = null
    try {
      const result = await fetchFn(0, limit)
      items.value = result.items
      total.value = result.total
      offset.value = result.items.length
      updateCache()
    } catch (e) {
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  // 加载更多
  const loadMore = async () => {
    if (isLoadingMore.value || !hasMore.value) return
    
    isLoadingMore.value = true
    try {
      const result = await fetchFn(offset.value, limit)
      items.value = [...items.value, ...result.items]
      total.value = result.total
      offset.value += result.items.length
      updateCache()
    } catch (e) {
      console.error('加载更多失败', e)
    } finally {
      isLoadingMore.value = false
    }
  }

  // 重置并重新加载（强制刷新，忽略缓存）
  const refresh = async () => {
    cache.value = null // 清除缓存
    offset.value = 0
    items.value = []
    await loadInitial()
  }

  // 滚动监听
  const scrollContainer = ref<HTMLElement | Window | null>(null)
  let ticking = false // RAF 节流标志

  const handleScrollCore = () => {
    ticking = false
    if (!scrollContainer.value || isLoadingMore.value || !hasMore.value) return

    let scrollTop: number
    let scrollHeight: number
    let clientHeight: number

    if (scrollContainer.value === window) {
      scrollTop = window.scrollY || document.documentElement.scrollTop
      scrollHeight = document.documentElement.scrollHeight
      clientHeight = window.innerHeight
    } else {
      const el = scrollContainer.value as HTMLElement
      scrollTop = el.scrollTop
      scrollHeight = el.scrollHeight
      clientHeight = el.clientHeight
    }

    // 距离底部小于阈值时加载更多
    if (scrollHeight - scrollTop - clientHeight < threshold) {
      loadMore()
    }
  }

  const handleScroll = () => {
    // 使用 RAF 节流，避免频繁触发布局计算
    if (!ticking) {
      requestAnimationFrame(handleScrollCore)
      ticking = true
    }
  }

  // 设置滚动容器（默认为 window）
  const setScrollContainer = (el: HTMLElement | Window | null) => {
    // 移除旧监听
    if (scrollContainer.value) {
      scrollContainer.value.removeEventListener('scroll', handleScroll)
    }
    
    scrollContainer.value = el || window
    
    // 添加新监听
    if (scrollContainer.value) {
      scrollContainer.value.addEventListener('scroll', handleScroll, { passive: true })
    }
  }

  // 自动设置 window 作为滚动容器
  onMounted(() => {
    setScrollContainer(window)
  })

  onUnmounted(() => {
    if (scrollContainer.value) {
      scrollContainer.value.removeEventListener('scroll', handleScroll)
    }
  })

  return {
    items,
    total,
    isLoading,
    isLoadingMore,
    hasMore,
    error,
    loadInitial,
    loadMore,
    refresh,
    setScrollContainer
  }
}
